import { Box, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { TZDate } from '@repo/tzdate';
import { useEffect, useState, type JSX } from 'react';
import UpcomingDaySelector from 'src/components/domain/upcoming-tours/DaySelector/UpcomingDaySelector';
import { getCustomOrDefaultText, useLocale } from '@repo/i18n';
import { capitalize } from '@repo/common-utils/TextUtils';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import { useUpcomingTours } from '@repo/widget-utils/services/hooks/product';
import { ProductInstance, UserTextCustomizations } from '@repo/types';

export type UpcomingToursProps = {
    useMonths: boolean;
    productCatalogIds: number[];
    productCatalogUrls: string[];
    numberOfDaysToDisplay: number;
    fewLeft: number;
    titleTextCustomizationKey?: keyof UserTextCustomizations;
    disableTitle: boolean;
    disableTogglebuttons: boolean;
    showRemaining?: boolean;
    startDate?: TZDate;
    daySelectorStyle: string;
    allowClosed?: boolean;
    toursList: (
        fewLeft: number,
        upcomingTours: ProductInstance[],
        showRemaining?: boolean,
    ) => JSX.Element;
    bigImages?: boolean;
};

type UpcomingToursContentProps = Omit<
    UpcomingToursProps,
    'productCatalogIds' | 'productCatalogUrls' | 'numberOfDaysToDisplay' | 'disableTitle'
> & {
    tours: ProductInstance[];
    title?: string;
    isLoading: boolean;
};

export function UpcomingToursBase(props: UpcomingToursProps) {
    const {
        productCatalogIds,
        productCatalogUrls,
        numberOfDaysToDisplay,
        titleTextCustomizationKey,
        disableTitle,
        startDate = TZDate.now(),
        allowClosed,
    } = props;

    const config = useConfigurations();
    const { t, locale } = useLocale();
    const endDate = startDate.add(numberOfDaysToDisplay, 'day');

    const { data: tours, isLoading } = useUpcomingTours(
        productCatalogIds,
        startDate,
        endDate,
        productCatalogUrls,
        allowClosed ?? config.includeClosedAvailability,
    );

    return (
        <UpcomingToursContent
            {...props}
            tours={tours}
            isLoading={isLoading}
            title={
                disableTitle
                    ? undefined
                    : capitalize(
                          getCustomOrDefaultText(
                              config.textCustomizations,
                              titleTextCustomizationKey,
                              locale,
                              t.upcoming_tours_title,
                          ),
                      )
            }
        ></UpcomingToursContent>
    );
}

function UpcomingToursContent({
    tours,
    useMonths,
    fewLeft,
    title,
    disableTogglebuttons,
    showRemaining,
    toursList,
    daySelectorStyle,
    isLoading,
}: UpcomingToursContentProps) {
    const customizations = useCustomizations();
    const theme = useTheme();
    const [shownTours, setShownTours] = useState<ProductInstance[]>(tours);
    const [days, setDays] = useState<TZDate[]>([]);
    const [day, setDay] = useState(TZDate.now());

    useEffect(() => {
        const days = tours
            .map((x) => x.start)
            .sort((a, b) => (a.isAfter(b) ? 1 : -1))
            .reduce<TZDate[]>((unique: TZDate[], day, index) => {
                if (index > 0) {
                    if (unique[unique.length - 1].isSame(day, useMonths ? 'month' : 'day'))
                        return unique;
                }
                unique.push(day);
                return unique;
            }, []);

        setDays(days);

        const day = days.length > 0 ? days[0] : TZDate.now();
        setDay(day);

        setShownTours(
            disableTogglebuttons
                ? tours
                : tours.filter((x) => x.start.isSame(day, useMonths ? 'month' : 'day')),
        );
    }, [disableTogglebuttons, tours, useMonths]);

    const onChangeDay = (day: TZDate) => {
        setDay(day);
        setShownTours(
            disableTogglebuttons
                ? tours
                : tours.filter((x) => x.start.isSame(day, useMonths ? 'month' : 'day')),
        );
    };

    return (
        <Box
            color="white"
            display="flex"
            flexDirection="column"
            alignItems="center"
            padding={theme.spacing(1)}
            sx={{
                backgroundColor: customizations.upcomingToursBackgroundColor,
            }}
        >
            {title && title.length > 0 && (
                <Typography sx={{ color: customizations.upcomingToursTitleTextColor }} variant="h2">
                    {title}
                </Typography>
            )}
            <Stack width="100%" maxWidth="1000px" alignItems="center">
                {isLoading ? (
                    <UpcomingToursSkeleton showDaySelector={!disableTogglebuttons} />
                ) : (
                    <>
                        {!disableTogglebuttons && (
                            <UpcomingDaySelector
                                day={day}
                                onDayChange={onChangeDay}
                                days={days}
                                useMonths={useMonths}
                                daySelectorStyle={daySelectorStyle}
                            ></UpcomingDaySelector>
                        )}
                        {toursList(fewLeft, shownTours, showRemaining)}
                    </>
                )}
            </Stack>
        </Box>
    );
}

function UpcomingToursSkeleton({ showDaySelector }: { showDaySelector: boolean }) {
    const theme = useTheme();

    return (
        <Stack gap={2} width="100%" alignItems="center" padding={theme.spacing(2, 0)}>
            {showDaySelector && (
                <Stack direction="row" gap={1}>
                    {[...Array(3)].map((_, i) => (
                        <Skeleton
                            key={i}
                            animation="pulse"
                            variant="rounded"
                            width={90}
                            height={42}
                        />
                    ))}
                </Stack>
            )}
            {[...Array(3)].map((_, i) => (
                <Stack
                    key={i}
                    direction="row"
                    alignItems="center"
                    gap={2}
                    width="100%"
                    padding={theme.spacing(2)}
                    sx={{ backgroundColor: theme.palette.background.paper }}
                >
                    <Skeleton
                        animation="pulse"
                        variant="rectangular"
                        width={120}
                        height={72}
                        sx={{ flexShrink: 0 }}
                    />
                    <Stack gap={1} flexGrow={1}>
                        <Skeleton
                            animation="pulse"
                            width="50%"
                            height={24}
                            sx={{ transform: 'scale(1)' }}
                        />
                        <Skeleton
                            animation="pulse"
                            width="25%"
                            height={20}
                            sx={{ transform: 'scale(1)' }}
                        />
                    </Stack>
                    <Stack gap={1} alignItems="flex-end">
                        <Skeleton animation="pulse" variant="rounded" width={96} height={20} />
                        <Skeleton
                            animation="pulse"
                            width={150}
                            height={20}
                            sx={{ transform: 'scale(1)' }}
                        />
                    </Stack>
                </Stack>
            ))}
        </Stack>
    );
}
